// src/components/scenarios/ScenarioForm.js
import React, { useEffect, useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useScenarios } from '../../context/ScenarioContext';
import { useBioData } from '../../context/BioDataContext';
import DeferredEligibilityChecker from './DeferredEligibilityChecker';
import BenefitStartCalculator from './BenefitStartCalculator';
import DeferredFields from './DeferredFields';
import DeferredEducationalContent from './DeferredEducationalContent';
import VeraEducationalContent from './VeraEducationalContent';
import './ScenarioForm.css';

function ScenarioForm({ scenario }) {
  const { handleScenarioChange } = useScenarios();
  const { bioData, calculateRetirementStats } = useBioData();
  const currentYear = new Date().getFullYear();
  const [selectedTspWithdrawalStartYear, setSelectedTspWithdrawalStartYear] = useState(null);
  const [selectedRetirementYearDate, setSelectedRetirementYearDate] = useState(null);
  const [eligibilityStatus, setEligibilityStatus] = useState(null);
  
  // Calculate user's age based on a specific year
  const calculateAge = (year) => {
    if (!bioData.dateOfBirth) return null;
    const birthYear = new Date(bioData.dateOfBirth).getFullYear();
    return year - birthYear;
  };
  
  // Current age calculation (for TSP limits)
  const currentAge = calculateAge(currentYear);

  // --- useEffect to update calculated fields --- 
  useEffect(() => {
    if (scenario.retirementYear) {
      // Use the context's calculateRetirementStats function
      const { retirementAge, yearsOfService } = calculateRetirementStats(scenario.retirementYear);

      // Check if the values actually changed before updating state 
      if (retirementAge !== scenario.retirementAge) {
        handleScenarioChange(scenario.id, 'retirementAge', retirementAge);
      }
      if (yearsOfService !== scenario.yearsOfService) {
        console.log(`Updating yearsOfService for scenario ${scenario.id}:`, yearsOfService);
        handleScenarioChange(scenario.id, 'yearsOfService', yearsOfService);
      }
    }

  // Dependencies: Re-run when these values change
  }, [ 
    scenario.retirementYear, 
    bioData.dateOfBirth, 
    bioData.entryOnDuty, // Use entryOnDuty instead of serviceStartDate
    scenario.id, 
    handleScenarioChange, 
    scenario.retirementAge,
    scenario.yearsOfService,
    calculateRetirementStats // Add the calculation function to dependencies
  ]);
  // --- End useEffect ---
  
  // Calculate maximum allowed TSP contribution percentage
  const calculateMaxTspPercentage = (estimatedSalary) => {
    if (!estimatedSalary || estimatedSalary <= 0) return 100;
    
    let maxContribution = 23500; // Default 2025 limit
    
    if (currentAge >= 50) {
      if (currentAge >= 60 && currentAge <= 63) {
        maxContribution = 23500 + 11250; // Higher catch-up for ages 60-63
      } else {
        maxContribution = 23500 + 7500; // Regular catch-up for 50-59 and 64+
      }
    }
    
    // Convert to percentage of salary
    return Math.min(100, Math.round((maxContribution / estimatedSalary) * 100));
  };
  
  // Get the estimated salary
  const estimatedSalary = 100000; // This should be replaced with actual calculation
  const maxTspPercentage = calculateMaxTspPercentage(estimatedSalary);
  
  // Check if current contribution exceeds limit
  const isTspContributionTooHigh = scenario.tspContribution > maxTspPercentage;
  
  // Handle maximum contribution toggle
  const handleMaxContributionToggle = (checked) => {
    if (checked) {
      handleScenarioChange(scenario.id, 'tspContribution', maxTspPercentage);
    }
    handleScenarioChange(scenario.id, 'maximizeTspContribution', checked);
  };

  // Effect to initialize and sync selectedTspWithdrawalStartYear with scenario.tspWithdrawalStartAge
  useEffect(() => {
    if (scenario.tspWithdrawalStartAge && bioData.dateOfBirth) {
      const birthYear = dayjs(bioData.dateOfBirth).year();
      const withdrawalYear = birthYear + scenario.tspWithdrawalStartAge;
      setSelectedTspWithdrawalStartYear(dayjs().year(withdrawalYear).month(0).date(1)); // Set to start of that year
    } else {
      // If no age, or no DOB, try to set based on retirement year if available
      if (scenario.retirementYear) {
         // setSelectedTspWithdrawalStartYear(dayjs().year(scenario.retirementYear).month(0).date(1));
         // Let's keep it null and let minDate handle the initial view, or user explicitly sets it.
         // This avoids presetting a year if the age field was intentionally blank.
      } else {
        setSelectedTspWithdrawalStartYear(null);
      }
    }
  }, [scenario.tspWithdrawalStartAge, bioData.dateOfBirth, scenario.retirementYear]);

  const handleTspWithdrawalYearChange = (newYearDate) => {
    setSelectedTspWithdrawalStartYear(newYearDate);
    if (newYearDate && bioData.dateOfBirth) {
      const birthYear = dayjs(bioData.dateOfBirth).year();
      const selectedYear = newYearDate.year();
      const newAge = selectedYear - birthYear;
      handleScenarioChange(scenario.id, 'tspWithdrawalStartAge', newAge);
    } else if (!newYearDate) {
      // If date is cleared, set age to undefined or a default (e.g., retirement age if that's desired)
      // For now, let's clear it, assuming blank means user will set it or it defaults elsewhere.
      handleScenarioChange(scenario.id, 'tspWithdrawalStartAge', undefined); 
    }
  };

  // Effect to initialize and sync selectedRetirementYearDate with scenario.retirementYear
  useEffect(() => {
    if (scenario.retirementYear) {
      setSelectedRetirementYearDate(dayjs().year(scenario.retirementYear).month(0).date(1));
    } else {
      setSelectedRetirementYearDate(null);
    }
  }, [scenario.retirementYear]);

  const handleRetirementYearChange = (newYearDate) => {
    setSelectedRetirementYearDate(newYearDate);
    if (newYearDate) {
      const newYear = newYearDate.year();
      handleScenarioChange(scenario.id, 'retirementYear', newYear);
      // The existing useEffect for retirementYear will handle recalculating age and service years.
    } else {
      handleScenarioChange(scenario.id, 'retirementYear', undefined); 
    }
  };
  
  const birthDateDayjs = bioData.dateOfBirth ? dayjs(bioData.dateOfBirth) : null;
  let minWithdrawalYearDate = null;
  let maxWithdrawalYearDate = null;
  const displayMinAge = scenario.retirementAge || 50; // Minimum age for TSP withdrawal (uses retirement age when available)

  // --- Retirement Year Date Boundaries ---
  let minRetirementYearDate = null;
  let maxRetirementYearDate = null;
  let minRetirementAgeForCalc = bioData.mra || 57; // Default to MRA or 57
  let minRetirementAgeInfo = `MRA (${minRetirementAgeForCalc})`;

  if (scenario.pensionType === 'special') {
    minRetirementAgeForCalc = 50;
    minRetirementAgeInfo = `Age 50 (Special FERS)`;
  } else if (scenario.pensionType === 'vera') {
    let veraMinRetirementAge = null;
    if (bioData.dateOfBirth && bioData.entryOnDuty && birthDateDayjs) {
      const birthD = birthDateDayjs;
      const sickLeaveYrs = (parseFloat(scenario.sickLeaveHours) || 0) / 2087.0;
      const ageAtEOD = dayjs(bioData.entryOnDuty).diff(birthD, 'year');

      // Iterate from a plausible start age (e.g., age at EOD or a practical minimum like 40) up to a max age (e.g., 70)
      for (let currentCheckAge = Math.max(18, ageAtEOD); currentCheckAge <= 70; currentCheckAge++) {
        const currentCheckYear = birthD.year() + currentCheckAge;
        // Ensure calculateRetirementStats can handle string or number for year
        const stats = calculateRetirementStats(currentCheckYear.toString()); 

        if (stats && typeof stats.yearsOfService === 'number') {
          const totalServiceAtCheckAge = stats.yearsOfService + sickLeaveYrs;
          const meets5020Rule = currentCheckAge >= 50 && totalServiceAtCheckAge >= 20;
          const meetsAnyAge25Rule = totalServiceAtCheckAge >= 25;

          if (meets5020Rule || meetsAnyAge25Rule) {
            veraMinRetirementAge = currentCheckAge;
            break; // Found the earliest age
          }
        }
      }
    }

    if (veraMinRetirementAge !== null) {
      minRetirementAgeForCalc = veraMinRetirementAge;
      minRetirementAgeInfo = `Age ${veraMinRetirementAge} (VERA)`;
    } else {
      // Fallback if bioData incomplete or not eligible by age 70 under VERA
      minRetirementAgeForCalc = 50; // Default VERA min age
      minRetirementAgeInfo = `VERA (Eligibility check needed)`;
    }
  }

  if (birthDateDayjs) {
    minRetirementYearDate = birthDateDayjs.add(minRetirementAgeForCalc, 'year').startOf('year');
    maxRetirementYearDate = birthDateDayjs.add(75, 'year').startOf('year'); // Max retirement age 75
  } else {
    minRetirementYearDate = dayjs().year(currentYear).startOf('year');
    maxRetirementYearDate = dayjs().year(currentYear + 40).startOf('year');
  }
  // --- End Retirement Year Date Boundaries ---

  // Calculate TSP withdrawal date boundaries
  if (birthDateDayjs) {
    // Use retirement age as minimum (displayMinAge already equals scenario.retirementAge when set)
    minWithdrawalYearDate = birthDateDayjs.add(displayMinAge, 'year').startOf('year');
    maxWithdrawalYearDate = birthDateDayjs.add(100, 'year').startOf('year'); // Max age 100
  } else if (scenario.retirementYear) {
    // If no DOB, but retirement year is known, use it as a minimum
    minWithdrawalYearDate = dayjs().year(scenario.retirementYear).startOf('year');
    maxWithdrawalYearDate = dayjs().year(scenario.retirementYear + 40).startOf('year'); // Retirement year + 40 years
  } else {
    // Fallback if neither DOB nor retirement year is known
    minWithdrawalYearDate = dayjs().year(currentYear).startOf('year');
    maxWithdrawalYearDate = dayjs().year(currentYear + 50).startOf('year');
  }

  return (
    <>
      {/* Pension Type Selector - Moved to top */}
      <div className="form-row">
        <div className="form-group compact">
          <label htmlFor={`pensionType-${scenario.id}`}>Pension Type:</label>
          <select
            id={`pensionType-${scenario.id}`}
            value={scenario.pensionType || ''}
            onChange={(e) => handleScenarioChange(scenario.id, 'pensionType', e.target.value)}
          >
            <option value="standard">Standard FERS</option>
            <option value="special">Special FERS (LEO, FF, etc.)</option>
            <option value="vera">VERA (Early Retirement)</option>
            <option value="deferred">Deferred FERS</option>
          </select>
        </div>
      </div>
      {scenario.pensionType !== 'deferred' && (
        <div className="form-row">
          <div className="form-group compact">
            <DatePicker
              label={scenario.retirementAge !== undefined && scenario.retirementAge !== null 
                ? `Retirement Age: ${scenario.retirementAge}` 
                : 'Retirement Age'}
              views={['year']}
              value={selectedRetirementYearDate}
              onChange={handleRetirementYearChange}
              minDate={minRetirementYearDate}
              maxDate={maxRetirementYearDate}
              slotProps={{
                textField: {
                  id: `retirementYear-${scenario.id}`,
                  variant: 'outlined',
                  size: 'small',
                  fullWidth: true,
                  helperText: selectedRetirementYearDate 
                    ? `Year: ${selectedRetirementYearDate.year()}. Min age: ${minRetirementAgeInfo}. Service: ${scenario.yearsOfService !== undefined ? scenario.yearsOfService : 'N/A'}` 
                    : `Min age: ${minRetirementAgeInfo}. Select year.`,
                  sx: { minWidth: 220 }
                }
              }}
            />
            {scenario.pensionType === 'standard' && scenario.retirementAge >= 62 && scenario.yearsOfService >= 20 && (
              <span className="bonus-info">Eligible for enhanced 1.1% multiplier</span>
            )}
          </div>
        </div>
      )}
      
      {/* Only show Years of Service for non-deferred pension types */}
      {scenario.pensionType !== 'deferred' && (
        <div className="form-row">
          <div className="form-group compact">
            <label htmlFor={`yearsOfService-${scenario.id}`}>Years of Service:</label>
            <input
              type="text"
              id={`yearsOfService-${scenario.id}`}
              value={scenario.yearsOfService !== null ? scenario.yearsOfService : 'N/A'}
              readOnly
              disabled
              className="number-input calculated-field"
            />
            <span className="help-text">Based on entry date and retirement/departure year</span>
          </div>
        </div>
      )}
      
      {scenario.pensionType === 'deferred' && (
        <>
          <DeferredFields 
            scenario={scenario} 
            onScenarioChange={handleScenarioChange}
          />
          <DeferredEligibilityChecker 
            scenario={scenario}
            onStatusChange={setEligibilityStatus}
          />
          
          {eligibilityStatus?.isEligible && (
            <BenefitStartCalculator
              scenario={scenario}
              eligibilityStatus={eligibilityStatus}
              onDeferredRetirementAgeChange={(age) => 
                handleScenarioChange(scenario.id, 'deferredRetirementAge', age)
              }
            />
          )}
          
          <DeferredEducationalContent />
        </>
      )}
      
      {scenario.pensionType === 'vera' && (
        <>
          {/* VERA Eligibility Check */}
          {(scenario.retirementAge >= 50 && scenario.yearsOfService < 20) && (
            <div className="eligibility-warning">
              <span className="warning-icon">⚠️</span>
              <span className="warning-text">
                <strong>VERA Eligibility Warning:</strong> You're age {scenario.retirementAge} but have only {scenario.yearsOfService ? scenario.yearsOfService.toFixed(1) : 0} years of service. VERA requires at least 20 years of service if you're age 50 or older.
              </span>
            </div>
          )}
          {(scenario.retirementAge < 50 && scenario.yearsOfService < 25) && (
            <div className="eligibility-warning">
              <span className="warning-icon">⚠️</span>
              <span className="warning-text">
                <strong>VERA Eligibility Warning:</strong> You're under age 50 with {scenario.yearsOfService ? scenario.yearsOfService.toFixed(1) : 0} years of service. VERA requires at least 25 years of service for retirement before age 50.
              </span>
            </div>
          )}
          <VeraEducationalContent />
        </>
      )}
      
      <div className="form-row">
        <div className="form-group compact">
          <label htmlFor={`survivorBenefit-${scenario.id}`}>Survivor Benefit:</label>
          <select
            id={`survivorBenefit-${scenario.id}`}
            value={scenario.survivorBenefit || ''}
            onChange={(e) => handleScenarioChange(scenario.id, 'survivorBenefit', parseInt(e.target.value))}
          >
            <option value={0}>None (0%)</option>
            <option value={25}>25% Survivor Benefit (5% reduction)</option>
            <option value={50}>50% Survivor Benefit (10% reduction)</option>
          </select>
        </div>
        
        <div className="form-group compact">
          <label htmlFor={`withdrawalRate-${scenario.id}`}>TSP Withdrawal Rate (%):</label>
          <input
            type="number"
            id={`withdrawalRate-${scenario.id}`}
            value={scenario.withdrawalRate || ''}
            onChange={(e) => handleScenarioChange(scenario.id, 'withdrawalRate', parseFloat(e.target.value))}
            step="0.1"
            min="0"
            max="20"
            className="number-input"
          />
        </div>
      </div>
      
      {/* TSP Withdrawal Start Age */}
      {scenario.pensionType !== 'deferred' && (
        <div className="form-row">
          <div className="form-group compact">
            <DatePicker
              label={scenario.tspWithdrawalStartAge !== undefined && scenario.tspWithdrawalStartAge !== null 
                ? `TSP Withdrawal Start Age: ${scenario.tspWithdrawalStartAge}` 
                : 'TSP Withdrawal Start Age'}
              views={['year']}
              value={selectedTspWithdrawalStartYear}
              onChange={handleTspWithdrawalYearChange}
              minDate={minWithdrawalYearDate}
              maxDate={maxWithdrawalYearDate}
              slotProps={{
                textField: {
                  id: `tspWithdrawalStartYear-${scenario.id}`,
                  variant: 'outlined',
                  size: 'small',
                  fullWidth: true,
                  helperText: selectedTspWithdrawalStartYear 
                    ? scenario.retirementYear && scenario.retirementAge
                      ? `You can withdraw starting ${selectedTspWithdrawalStartYear.year()} (age ${scenario.tspWithdrawalStartAge || scenario.retirementAge}). Retirement set for ${scenario.retirementYear} at age ${scenario.retirementAge}.`
                      : `Withdrawal year: ${selectedTspWithdrawalStartYear.year()}${scenario.tspWithdrawalStartAge ? ` (age ${scenario.tspWithdrawalStartAge})` : ''}`
                    : scenario.retirementYear && scenario.retirementAge 
                      ? `You can withdraw from TSP at retirement (${scenario.retirementYear}, age ${scenario.retirementAge})`
                      : `Minimum withdrawal age: ${displayMinAge}. Select a year.`,
                  sx: { minWidth: 220 },
                  // The DatePicker input will show the selected year by default.
                  // The label now carries the primary age information.
                }
              }}
            />
            {/* <span className="help-text">Select year to begin TSP withdrawals (default: retirement year)</span> */}
          </div>
        </div>
      )}

      {/* TSP Contribution */}
      <div className="form-row">
        <div className="form-group compact">
          <label htmlFor={`tspContribution-${scenario.id}`}>
            TSP Contribution (%):
            {isTspContributionTooHigh && (
              <div className="tooltip">
                <span className="tooltip-icon error">!</span>
                <span className="tooltip-text">
                  For 2025, the IRS set the regular (elective deferral) limit at <strong>$23,500</strong>. If you're age 50 or older, you can also make catch‑up contributions:<br/>
                  • <strong>Ages 50–59 and age 64+</strong>: An extra <strong>$7,500</strong> (total of <strong>$31,000</strong>).<br/>
                  • <strong>Ages 60–63</strong>: A higher catch‑up of <strong>$11,250</strong> (total of <strong>$34,750</strong>).<br/>
                  These limits apply to employee contributions only and do not include agency matching contributions.
                </span>
              </div>
            )}
            {scenario.tspContribution < 5 && (
              <div className="tooltip">
                <span className="tooltip-icon warning">!</span>
                <span className="tooltip-text">
                  You're missing out on Agency matching contributions, we recommend you set your TSP contribution to at least 5%.
                </span>
              </div>
            )}
          </label>
          <input
            type="number"
            id={`tspContribution-${scenario.id}`}
            value={scenario.tspContribution || ''}
            onChange={(e) => {
              const value = parseFloat(e.target.value);
              let agencyMatch = 0;
              
              // Calculate agency match based on TSP contribution
              if (value >= 5) {
                agencyMatch = 5; // Maximum match (1% automatic + 4% matching)
              } else if (value > 0) {
                agencyMatch = 1; // 1% automatic contribution
                if (value <= 3) {
                  agencyMatch += value; // Dollar-for-dollar on first 3%
                } else {
                  agencyMatch += 3; // Full match on first 3%
                  agencyMatch += (value - 3) * 0.5; // Half match on next 2%
                }
              } else {
                agencyMatch = 1; // Just the 1% automatic contribution
              }
              
              handleScenarioChange(scenario.id, 'tspContribution', value);
              handleScenarioChange(scenario.id, 'agencyMatch', agencyMatch);
            }}
            min="0"
            max="100"
            step="0.5"
            className="number-input"
          />
          <span className="help-text">Your contribution as percentage of salary</span>
        </div>
        
        <div className="form-group toggle-group">
          {/* Note: We use nesting here instead of htmlFor/id for the toggle */}
          <label>
            <input
              type="checkbox"
              checked={scenario.maximizeTspContribution}
              onChange={(e) => handleMaxContributionToggle(e.target.checked)}
            />
            Maximum Contribution:
          </label>
          <span className="help-text">
            Maximize yearly TSP contribution based on IRS limits
          </span>
        </div>
      </div>
      
      <div className="form-row">
        <div className="form-group compact">
          <label htmlFor={`agencyMatch-${scenario.id}`}>Agency Match (%):</label>
          <input
            type="text"
            id={`agencyMatch-${scenario.id}`}
            value={scenario.agencyMatch || ''}
            readOnly
            disabled
            className="number-input calculated-field"
          />
          <span className="help-text">Automatic 1% + up to 4% matching</span>
        </div>
      </div>

    </>
  );
}

export default ScenarioForm;